import React, { Suspense, lazy } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { useAuth } from '../redux/hooks/useAuth';
import AuthLoadingScreen from '../components/AuthLoadingScreen';
import ProtectedRoute from '../components/ProtectedRoute';

// Consolidated Layouts
import MainLayout from '../layout/MainLayout';
import AuthLayout from '../layout/AuthLayout';

// Loadable wrapper similar to reference project
const Loadable = (Component) => (props) => (
  <Suspense fallback={<AuthLoadingScreen type="loading" message="Loading page..." />}>
    <Component {...props} />
  </Suspense>
);

// Lazy-loaded pages with preserved paths
const LoginPage = Loadable(lazy(() => import('../pages/auth/LoginPage')));
const DashboardPage = Loadable(lazy(() => import('../pages/main/DashboardPage')));
const CandidatesPage = Loadable(lazy(() => import('../pages/main/CandidatesPage')));
const JobDescriptionsPage = Loadable(lazy(() => import('../pages/main/JobDescriptionsPage')));
const AnalyticsPage = Loadable(lazy(() => import('../pages/main/AnalyticsPage')));
const SettingsPage = Loadable(lazy(() => import('../pages/main/SettingsPage')));
const SolutionsPage = Loadable(lazy(() => import('../pages/main/SolutionsPage')));
const UnifiedAdminPanel = Loadable(lazy(() => import('../pages/admin/UnifiedAdminPanel')));
const PermissionDebugger = Loadable(lazy(() => import('../components/PermissionDebugger')));
const CandidateComponent = Loadable(lazy(() => import('../components/CandidateComponent')));
const InterviewManagementDashboard = Loadable(lazy(() => import('../components/InterviewManagementDashboard')));
const NotFoundPage = Loadable(lazy(() => import('../pages/NotFoundPage')));
const UnauthorizedPage = Loadable(lazy(() => import('../pages/UnauthorizedPage')));

export default function Router() {
  const { isAuthenticated, loading, initialized } = useAuth();
  
  console.log('🔄 Router: Component rendered', { isAuthenticated, loading, initialized });

  // Define routes configuration
  const routes = [
    { path: '/login', element: <Navigate to="/auth/login" replace /> },
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      ),
      children: [
        { index: true, element: <Navigate to="/job-descriptions" replace /> },
        { path: 'dashboard', element: (
          <ProtectedRoute requiredPermissions={['candidates:read']}>
            <DashboardPage />
          </ProtectedRoute>
        ) },
        { path: 'candidates', element: (
          <ProtectedRoute requiredPermissions={['candidates:read']}>
            <CandidatesPage />
          </ProtectedRoute>
        ) },
        { path: 'candidate-test', element: (
          <ProtectedRoute requiredPermissions={['candidates:read']}>
            <CandidateComponent />
          </ProtectedRoute>
        ) },
        { path: 'job-descriptions', element: (
          <ProtectedRoute requiredPermissions={['jds:read']}>
            <JobDescriptionsPage />
          </ProtectedRoute>
        ) },
        { path: 'analytics', element: (
          <ProtectedRoute requiredRoles={['super_admin']}>
            <AnalyticsPage />
          </ProtectedRoute>
        ) },
        { path: 'settings', element: <SettingsPage /> },
        { path: 'solutions', element: <SolutionsPage /> },
        { path: 'interviews', element: (
          <ProtectedRoute requiredPermissions={['interviews:read']}>
            <InterviewManagementDashboard />
          </ProtectedRoute>
        ) },
        // { path: 'multi-tenant-test', element: <MultiTenantTestPage /> },
        { path: 'debug-permissions', element: <PermissionDebugger /> },
        { path: 'admin', element: (
          <ProtectedRoute requiredPermissions={['admin:read', 'super_admin:read']}>
            <UnifiedAdminPanel />
          </ProtectedRoute>
        ) },
        { path: 'hr', element: (
          <ProtectedRoute requiredRoles={['admin', 'hr_manager', 'super_admin', 'client_admin', 'client_hr_manager']}>
            <div>HR Management (Coming Soon)</div>
          </ProtectedRoute>
        ) },
        { path: 'recruiter', element: (
          <ProtectedRoute requiredRoles={['admin', 'hr_manager', 'recruiter', 'super_admin', 'client_admin', 'client_hr_manager']}>
            <div>Recruiter Tools (Coming Soon)</div>
          </ProtectedRoute>
        ) },
      ],
    },
    {
      path: '/auth',
      element: !isAuthenticated ? <AuthLayout /> : <Navigate to="/job-descriptions" replace />,
      children: [
        { path: 'login', element: <LoginPage /> },
        { path: '*', element: <Navigate to="/auth/login" replace /> },
      ],
    },
    { path: '/unauthorized', element: <UnauthorizedPage /> },
    { path: '*', element: <NotFoundPage /> },
  ];

  // Always call useRoutes hook first, before any conditional returns
  const element = useRoutes(routes);

  // Show loading screen while authentication is being initialized
  if (!initialized || loading) {
    return <AuthLoadingScreen type="loading" message="Initializing application..." />;
  }

  // Return the routed element
  return element;
}